import type { PermissionCode } from '@/shared/permissions';

/**
 * Catalog permission codes — mirror the BFF's `RolePermissionMapper`:
 *  - `catalog.view` / `catalog.manage` — products, categories, media, import, search
 *  - `catalog.pricing.view` / `catalog.pricing.manage` — price governance
 *  - `catalog.structure.view` / `catalog.structure.manage` — PIM structure: the category tree's
 *    parent/move controls, the attribute dictionary, product families (attribute templates),
 *    measurement families, and the catalog language registry (epic-catalog-004/005)
 *  - `catalog.publication.view` / `catalog.publication.manage` — the publication workspace:
 *    publication catalogs (permanent/temporary), their include/exclude rules, sales-channel
 *    bindings (primary/overlay), and catalog-type publication profiles (epic-catalog-001)
 *
 * Note: this is the `catalog.*` namespace, distinct from the `settings.catalog.*`
 * settings domain. `settings.*` does NOT grant these.
 */
export const CATALOG_PERMISSIONS = {
  catalog: { view: 'catalog.view', manage: 'catalog.manage' },
  pricing: { view: 'catalog.pricing.view', manage: 'catalog.pricing.manage' },
  /**
   * `settings.pricing.manage` — adr-pricing-025 d9 (permissions-registry.md §2.3 row 4a):
   * "no new permission codes" for the price-list surfaces. This is the SAME code that already
   * gates the unrelated `PricingSettingsPage` (engine mode/rounding settings, `store-settings`
   * feature) — reused here (never redefined) to admin-gate the operations that repoint an entire
   * store's till prices: price-list store bind/unbind and list activate/deactivate. It is under
   * the `settings.*` wildcard, so it needs no mock-session seeding beyond what already exists.
   */
  pricingAdmin: { manage: 'settings.pricing.manage' },
  /**
   * `catalog.pricing.bulk` (adr-pricing-025 d9, permissions-registry §2.3 row 4/4a) — the
   * mass-change actions: bulk repricing batch dry-run/apply and markdown-plan activate/cancel.
   * Required IN ADDITION TO `catalog.pricing.manage`, not instead of it — the BFF mapper already
   * carried this code (`CatalogPricingControllerImpl`), the portal simply had no consumer of it
   * before plan-platform-002 WP4.3.
   */
  pricingBulk: { manage: 'catalog.pricing.bulk' },
  structure: { view: 'catalog.structure.view', manage: 'catalog.structure.manage' },
  publication: { view: 'catalog.publication.view', manage: 'catalog.publication.manage' },
} as const satisfies Record<string, Record<string, PermissionCode>>;

export type CatalogPermissionArea = keyof typeof CATALOG_PERMISSIONS;

/** Flattened list of every catalog permission code (for mock sessions / seeding). */
export const ALL_CATALOG_PERMISSIONS: PermissionCode[] = Object.values(CATALOG_PERMISSIONS).flatMap(
  (area) => Object.values(area),
);
